import CardHeader from "components/CardHeader";
import { Recurring } from "models/Recurring";
import React, { useEffect, useState } from "react";
import {
    Alert,
    Button,
    Card,
    CardBody,
    Col,
    FormGroup,
    Input,
    Label,
    Row,
    Spinner,
} from "reactstrap";
import * as api from "services/Api";
import parser from "cron-parser";
import InvoiceIntervalField, { describeInvoiceInterval } from "./InvoiceIntervalField";
import { useOfficeTranslation } from "i18n/OfficeTranslation";
import { OfficeTranslationKey } from "i18n/officeTranslations";

const createRecurring = (accountId: string): Recurring => Object.assign(new Recurring(), {
    customer: accountId,
    interval: "0 9 1 * *",
    type: "service",
    name: "",
    description: "",
    amount: 1,
    price: 0,
    uom: "units",
    tax_rate: 21,
});

const describeNextRun = (interval: string, locale: string): string => {
    try {
        return parser.parseExpression(interval).next().toDate().toLocaleString(locale);
    } catch {
        return "";
    }
};

const AccountRecurringsCard: React.FC<{ accountId: string }> = ({ accountId }) => {
    const { intlLocale, locale, t } = useOfficeTranslation();
    const [recurrings, setRecurrings] = useState<Recurring[]>([]);
    const [draft, setDraft] = useState<Recurring>(() => createRecurring(accountId));
    const [adding, setAdding] = useState(false);
    const [editingId, setEditingId] = useState("");
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState("");
    const [error, setError] = useState<OfficeTranslationKey | "">("");

    useEffect(() => {
        setLoading(true);
        setError("");
        setRecurrings([]);
        setAdding(false);
        setEditingId("");
        setDraft(createRecurring(accountId));
        api.getRecurrings()
            .then(items => setRecurrings((items ?? []).filter(recurring => recurring.customer === accountId)))
            .catch(() => setError("accounts.accountRecurringsCard.loadFailed"))
            .finally(() => setLoading(false));
    }, [accountId]);

    const updateDraft = (values: Partial<Recurring>) =>
        setDraft(current => Object.assign(new Recurring(), current, values));

    const cancel = () => {
        setAdding(false);
        setEditingId("");
        setDraft(createRecurring(accountId));
        setError("");
    };

    const edit = (recurring: Recurring) => {
        setAdding(false);
        setEditingId(recurring.id);
        setDraft(Object.assign(new Recurring(), recurring));
        setError("");
    };

    const save = async () => {
        if (
            !draft.name.trim()
            || !draft.interval.trim()
            || describeInvoiceInterval(draft.interval, locale) === null
            || !Number.isFinite(draft.amount)
            || draft.amount <= 0
            || !Number.isFinite(draft.price)
            || !Number.isFinite(draft.tax_rate)
            || draft.tax_rate < 0
            || draft.tax_rate > 100
        ) {
            setError("accounts.accountRecurringsCard.invalidForm");
            return;
        }

        setSaving(true);
        setError("");
        try {
            const candidate = Object.assign(new Recurring(), draft, { customer: accountId });
            const saved = editingId
                ? await api.updateRecurring(candidate)
                : await api.addRecurring(candidate);
            setRecurrings(current => editingId
                ? current.map(recurring => recurring.id === editingId ? saved : recurring)
                : [...current, saved]);
            setAdding(false);
            setEditingId("");
            setDraft(createRecurring(accountId));
        } catch {
            setError("accounts.accountRecurringsCard.saveFailed");
        } finally {
            setSaving(false);
        }
    };

    const remove = async (recurring: Recurring) => {
        if (!window.confirm(t("accounts.accountRecurringsCard.deleteRecurringName", {name: recurring.name}))) return;
        setDeleting(recurring.id);
        setError("");
        try {
            await api.deleteRecurring(recurring);
            setRecurrings(current => current.filter(item => item.id !== recurring.id));
        } catch {
            setError("accounts.accountRecurringsCard.deleteFailed");
        } finally {
            setDeleting("");
        }
    };

    return <Card>
        <CardBody>
            <CardHeader
                title={t("common.recurringItems")}
                icon="mdi-calendar-sync-outline"
                count={recurrings.length}
                actions={!adding && !editingId && <Button color="primary" size="sm" outline onClick={() => {
                    setAdding(true);
                    setError("");
                }}>
                    <i className="mdi mdi-plus me-1" />{t("common.add")}
                </Button>}
            />

            {error && <Alert color="danger" className="mt-3 mb-0">{t(error)}</Alert>}
            {loading && <div className="text-center py-3"><Spinner size="sm" /></div>}

            {!loading && recurrings.map(recurring => {
                const nextRun = describeNextRun(recurring.interval, intlLocale);
                return <div className="border-bottom py-3" key={recurring.id}>
                    <div className="d-flex justify-content-between align-items-start gap-3">
                        <div className="min-w-0">
                            <strong>{recurring.amount}× {recurring.name}</strong>
                            {recurring.description && <div className="text-muted small text-break">{recurring.description}</div>}
                            <div className="small mt-2">
                                <i className="mdi mdi-calendar-clock-outline text-primary me-1" />
                                {describeInvoiceInterval(recurring.interval, locale)
                                    ?? t("accounts.invoiceIntervalField.invalid")}
                            </div>
                            {nextRun && <div className="text-muted small">{t("common.nextRun")}: {nextRun}</div>}
                        </div>
                        <div className="text-end flex-shrink-0">
                            <div>{new Intl.NumberFormat(intlLocale, {style: "currency", currency: "EUR"}).format(recurring.amount * recurring.price)}</div>
                            <div className="text-muted small">{(recurring.tax_rate ?? 0).toFixed(2)}% {t("common.vat")}</div>
                            <div className="d-flex justify-content-end gap-2 mt-2">
                                <Button
                                    color="outline-primary"
                                    size="sm"
                                    style={{minWidth: 44, minHeight: 44}}
                                    disabled={saving || deleting === recurring.id}
                                    aria-label={t("accounts.accountRecurringsCard.editNamed", {name: recurring.name})}
                                    onClick={() => edit(recurring)}
                                >
                                    <i className="mdi mdi-pencil-outline" />
                                </Button>
                                <Button
                                    color="outline-danger"
                                    size="sm"
                                    style={{minWidth: 44, minHeight: 44}}
                                    disabled={saving || deleting === recurring.id}
                                    aria-label={t("accounts.accountRecurringsCard.deleteNamed", {name: recurring.name})}
                                    onClick={() => remove(recurring)}
                                >
                                    {deleting === recurring.id
                                        ? <Spinner size="sm" />
                                        : <i className="mdi mdi-delete-outline" />}
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>;
            })}

            {!loading && !recurrings.length && !adding && !editingId &&
                <p className="text-muted mt-3 mb-0">{t("accounts.accountRecurringsCard.noRecurringsConfiguredForThisAccount")}</p>}

            {(adding || editingId) && <div className="border rounded p-3 mt-3">
                <h6 className="mb-3">{t(editingId
                    ? "accounts.accountRecurringsCard.editRecurring"
                    : "accounts.accountRecurringsCard.addRecurring")}</h6>
                <FormGroup>
                    <Label for="recurring-name">{t("common.name")}</Label>
                    <Input
                        id="recurring-name"
                        value={draft.name}
                        onChange={event => updateDraft({ name: event.target.value })}
                    />
                </FormGroup>
                <FormGroup>
                    <Label for="recurring-description">{t("common.description")}</Label>
                    <Input
                        id="recurring-description"
                        value={draft.description}
                        onChange={event => updateDraft({ description: event.target.value })}
                    />
                </FormGroup>
                <Row>
                    <Col md={6}>
                        <FormGroup>
                            <Label for="recurring-type">{t("common.type")}</Label>
                            <Input
                                id="recurring-type"
                                value={draft.type}
                                onChange={event => updateDraft({ type: event.target.value })}
                            />
                        </FormGroup>
                    </Col>
                    <Col md={6}>
                        <FormGroup>
                            <Label for="recurring-uom">{t("accounts.accountRecurringsCard.unit")}</Label>
                            <Input
                                id="recurring-uom"
                                value={draft.uom}
                                onChange={event => updateDraft({ uom: event.target.value })}
                            />
                        </FormGroup>
                    </Col>
                    <Col md={6}>
                        <FormGroup>
                            <Label for="recurring-amount">{t("common.amount")}</Label>
                            <Input
                                id="recurring-amount"
                                type="number"
                                min={0}
                                step="any"
                                value={draft.amount}
                                onChange={event => updateDraft({ amount: Number(event.target.value) })}
                            />
                        </FormGroup>
                    </Col>
                    <Col md={6}>
                        <FormGroup>
                            <Label for="recurring-price">{t("common.price")}</Label>
                            <Input
                                id="recurring-price"
                                type="number"
                                min={0}
                                step="0.01"
                                value={draft.price}
                                onChange={event => updateDraft({ price: Number(event.target.value) })}
                            />
                        </FormGroup>
                    </Col>
                    <Col md={6}>
                        <FormGroup>
                            <Label for="recurring-tax-rate">{t("common.vatRatePercentage")}</Label>
                            <Input
                                id="recurring-tax-rate"
                                type="number"
                                min={0}
                                max={100}
                                step="0.01"
                                value={draft.tax_rate}
                                onChange={event => updateDraft({ tax_rate: Number(event.target.value) })}
                            />
                        </FormGroup>
                    </Col>
                </Row>
                <InvoiceIntervalField
                    value={draft.interval}
                    onChange={interval => updateDraft({ interval })}
                />
                <div className="d-flex justify-content-end gap-2">
                    <Button color="light" disabled={saving} onClick={cancel}>{t("common.cancel")}</Button>
                    <Button color="primary" disabled={saving} onClick={save}>
                        {saving && <Spinner size="sm" className="me-2" />}
                        {t("accounts.accountRecurringsCard.saveRecurring")}
                    </Button>
                </div>
            </div>}
        </CardBody>
    </Card>;
};

export default AccountRecurringsCard;
